import React, { useState, useEffect } from 'react';
import {
  X, Download, Printer, Calendar, RefreshCw, FileText, Filter, Clock
} from 'lucide-react';
import apiClient from '../services/api';
import { useNavigate } from 'react-router-dom';

const NAVY      = '#0d1c6b';
const NAVY_LIGHT = '#1a3a8f';

const DayBook = ({ onClose }) => {
  const navigate = useNavigate();
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState('');
  const [filters,    setFilters]    = useState({
    StartDate: new Date().toISOString().slice(0,10),
    EndDate:   new Date().toISOString().slice(0,10),
    VoucherType: '',
  });
  const [reportData, setReportData] = useState(null);

  function getSessionData() {
    try {
      const s = localStorage.getItem('ltms_session');
      if (!s) return null;
      const d = JSON.parse(s);
      return d?.session || d;
    } catch { return null; }
  }
  const session = getSessionData();

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleGenerateReport = async () => {
    if (!filters.StartDate || !filters.EndDate) { setError('Please select date range'); return; }
    setLoading(true); setError('');
    try {
      const res = await apiClient.get('/DayBook.php', { params: filters });
      if (res.data.success) setReportData(res.data.data);
      else setError(res.data.message || 'Failed to generate report');
    } catch (err) { setError('Error generating report: ' + (err.response?.data?.message || err.message)); }
    finally { setLoading(false); }
  };

  const handlePrint = () => window.print();

  const handleExportCSV = () => {
    if (!reportData?.vouchers?.length) { alert('No data to export'); return; }
    const rows = [];
    reportData.vouchers.forEach(v => {
      v.details.forEach((d, i) => {
        rows.push([v.TransDate||'', v.VoucherNo||'', v.VoucherType||'', d.ACHeadName||'', d.Debit||'0.00', d.Credit||'0.00', i===0?(v.Remarks||''):'']);
      });
      rows.push(['','','','','','','']);
    });
    const headers = ['Date','Voucher No','Type','Account Head','Debit','Credit','Remarks'];
    const csv = [headers.join(','), ...rows.map(r=>r.map(c=>`"${c}"`).join(','))].join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv],{type:'text/csv;charset=utf-8;'}));
    a.download = `day_book_${filters.StartDate}_to_${filters.EndDate}.csv`;
    a.style.visibility = 'hidden';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const handleClearFilters = () => {
    setFilters({ StartDate:new Date().toISOString().slice(0,10), EndDate:new Date().toISOString().slice(0,10), VoucherType:'' });
    setReportData(null);
  };

  // Voucher type badge color
  const voucherBadgeCls = (type) => {
    const t = (type||'').toLowerCase();
    if (t==='cv'||t==='cr') return 'bg-emerald-100 text-emerald-800';
    if (t==='bv'||t==='br') return 'bg-blue-100 text-blue-800';
    if (t==='jv')            return 'bg-violet-100 text-violet-800';
    return 'bg-gray-100 text-gray-700';
  };

  const inpCls = 'w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white outline-none transition-all duration-150 focus:border-blue-400 focus:shadow-[0_0_0_3px_rgba(59,130,246,0.1)] box-border font-[inherit]';
  const lblCls = 'block text-sm font-semibold text-gray-700 mb-1';

  if (!session?.companyid) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-5 pb-4 border-b-2 border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 m-0">Session Required</h2>
            <p className="text-sm text-gray-500 mt-1 m-0">Please select company and year</p>
          </div>
        </div>
        <div className="text-center py-20 text-gray-500">
          <div className="text-5xl mb-4">🔒</div>
          <h3 className="font-bold text-gray-700 mb-2">Session Required</h3>
          <p className="text-sm">Please select a company and financial year from the dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-5 max-w-[1100px] mx-auto box-border">
      <style>{`
        @media print {
          .no-print { display:none !important; }
          .print-only { display:flex !important; }
        }
        .print-only { display:none; }
      `}</style>

      {/* Page header */}
      <div className="flex items-center justify-between mb-5 pb-4 border-b-2 border-gray-200">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 m-0">Day Book</h2>
          <p className="text-sm text-gray-500 mt-1 m-0">Daily Transaction Register</p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex gap-2.5 flex-wrap mb-5 no-print">
        <button onClick={handleGenerateReport} disabled={loading}
          className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white border-0 rounded-lg cursor-pointer transition-all duration-150 hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ background:`linear-gradient(135deg,${NAVY},${NAVY_LIGHT})` }}>
          <RefreshCw size={18} /> Generate Report
        </button>
        <button onClick={handlePrint} disabled={!reportData || loading}
          className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-gray-600 border-0 rounded-lg cursor-pointer transition-all duration-150 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed">
          <Printer size={18} /> Print
        </button>
        <button onClick={handleExportCSV} disabled={!reportData || loading}
          className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-emerald-600 border-0 rounded-lg cursor-pointer transition-all duration-150 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed">
          <Download size={18} /> Export CSV
        </button>
        <button onClick={handleClearFilters} disabled={loading}
          className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg cursor-pointer transition-all duration-150 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
          <RefreshCw size={18} /> Clear
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-300 text-red-700 rounded-lg mb-4 text-sm no-print">
          <span className="text-lg">⚠</span><div><strong>Error:</strong> {error}</div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 mb-5 no-print">
        <h3 className="flex items-center gap-2 text-[0.95rem] font-semibold text-gray-800 mb-4 pb-3 border-b-2 border-gray-100 m-0">
          <Filter size={18} /> Report Filters
        </h3>
        <div className="grid grid-cols-3 gap-4 max-[640px]:grid-cols-1">
          <div>
            <label className={lblCls}>Start Date <span className="text-red-500">*</span></label>
            <input type="date" name="StartDate" value={filters.StartDate} onChange={handleFilterChange} required className={inpCls} />
          </div>
          <div>
            <label className={lblCls}>End Date <span className="text-red-500">*</span></label>
            <input type="date" name="EndDate" value={filters.EndDate} onChange={handleFilterChange} required className={inpCls} />
          </div>
          <div>
            <label className={lblCls}>Voucher Type</label>
            <select name="VoucherType" value={filters.VoucherType} onChange={handleFilterChange} className={`${inpCls} cursor-pointer`}>
              <option value="">All Types</option>
              <option value="CV">CV - Cash Payment</option>
              <option value="CR">CR - Cash Receipt</option>
              <option value="BV">BV - Bank Payment</option>
              <option value="BR">BR - Bank Receipt</option>
              <option value="JV">JV - Journal Voucher</option>
            </select>
          </div>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex flex-col items-center gap-3 py-16 text-gray-500">
          <span className="w-10 h-10 border-4 border-gray-200 border-t-blue-500 rounded-full block" style={{ animation:'spin 0.8s linear infinite' }} />
          <p className="m-0 text-sm">Generating day book...</p>
        </div>
      )}

      {/* Report */}
      {reportData && !loading && (
        <div>
          {/* Report title */}
          <div className="text-center mb-6 pb-5 border-b-2 border-gray-200">
            <h1 className="text-2xl font-extrabold text-gray-900 m-0 mb-1">
              {reportData.company?.CompanyName || 'Company Name'}
            </h1>
            <h2 className="text-lg font-bold text-gray-600 m-0 mb-3">Day Book</h2>
            <div className="flex items-center justify-center gap-6 flex-wrap text-sm text-gray-500">
              <span className="flex items-center gap-1.5">
                <Calendar size={16} />
                Period: {new Date(filters.StartDate).toLocaleDateString()} to {new Date(filters.EndDate).toLocaleDateString()}
              </span>
              <span className="flex items-center gap-1.5">
                <Clock size={16} />
                Generated: {new Date().toLocaleString()}
              </span>
            </div>
          </div>

          {/* Vouchers */}
          {reportData.vouchers?.length > 0 ? (
            reportData.vouchers.map((voucher, vIndex) => (
              <div key={vIndex} className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-4 shadow-sm">
                {/* Voucher header */}
                <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100"
                  style={{ background:`linear-gradient(135deg,${NAVY},${NAVY_LIGHT})` }}>
                  <span className="font-mono font-bold text-white text-sm">{voucher.VoucherNo}</span>
                  <span className={`px-2 py-0.5 rounded text-xs font-bold ${voucherBadgeCls(voucher.VoucherType)}`}>
                    {voucher.VoucherType}
                  </span>
                  <span className="text-white/80 text-sm">{new Date(voucher.TransDate).toLocaleDateString()}</span>
                </div>

                {/* Voucher table */}
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2.5 text-left font-semibold text-gray-600 text-xs uppercase tracking-wide border-b border-gray-200">Account Head</th>
                        <th className="px-4 py-2.5 text-right font-semibold text-gray-600 text-xs uppercase tracking-wide border-b border-gray-200 w-[150px]">Debit</th>
                        <th className="px-4 py-2.5 text-right font-semibold text-gray-600 text-xs uppercase tracking-wide border-b border-gray-200 w-[150px]">Credit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {voucher.details.map((detail, dIndex) => (
                        <tr key={dIndex} className="border-b border-gray-50 hover:bg-gray-50 transition-colors duration-75">
                          <td className="px-4 py-2.5">
                            <span className="font-medium text-gray-800">{detail.ACHeadName}</span>
                            {detail.Remarks && (
                              <div className="text-[12px] text-gray-500 mt-1 italic">{detail.Remarks}</div>
                            )}
                          </td>
                          <td className="px-4 py-2.5 text-right">
                            {parseFloat(detail.Debit||0) > 0
                              ? <span className="font-bold text-red-600 font-mono">{parseFloat(detail.Debit).toFixed(2)}</span>
                              : <span className="text-gray-300">-</span>}
                          </td>
                          <td className="px-4 py-2.5 text-right">
                            {parseFloat(detail.Credit||0) > 0
                              ? <span className="font-bold text-emerald-700 font-mono">{parseFloat(detail.Credit).toFixed(2)}</span>
                              : <span className="text-gray-300">-</span>}
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-gray-50 font-bold text-[15px]">
                        <td className="px-4 py-2.5 text-gray-600 font-semibold">Total:</td>
                        <td className="px-4 py-2.5 text-right font-mono font-bold text-red-600">{parseFloat(voucher.TotalDebit||0).toFixed(2)}</td>
                        <td className="px-4 py-2.5 text-right font-mono font-bold text-emerald-700">{parseFloat(voucher.TotalCredit||0).toFixed(2)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Voucher remarks */}
                {voucher.Remarks && (
                  <div className="px-4 py-2.5 bg-gray-50 border-t border-gray-100 text-sm text-gray-600">
                    <strong>Remarks:</strong> {voucher.Remarks}
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="text-center py-16 text-gray-500">
              <div className="text-5xl mb-4">📅</div>
              <h3 className="font-bold text-gray-700 mb-2">No Transactions Found</h3>
              <p className="text-sm">No transactions found for the selected period</p>
            </div>
          )}

          {/* Summary cards */}
          {reportData.summary && (
            <div className="grid grid-cols-3 gap-4 mt-8 max-[640px]:grid-cols-1">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 text-center">
                <div className="text-xs font-bold uppercase tracking-wide text-blue-600 mb-1">Total Vouchers</div>
                <div className="text-2xl font-extrabold text-blue-800">{reportData.summary.TotalVouchers||0}</div>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 text-center">
                <div className="text-xs font-bold uppercase tracking-wide text-emerald-600 mb-1">Total Debit</div>
                <div className="text-2xl font-extrabold text-emerald-800 font-mono">{parseFloat(reportData.summary.TotalDebit||0).toFixed(2)}</div>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-xl p-5 text-center">
                <div className="text-xs font-bold uppercase tracking-wide text-red-600 mb-1">Total Credit</div>
                <div className="text-2xl font-extrabold text-red-700 font-mono">{parseFloat(reportData.summary.TotalCredit||0).toFixed(2)}</div>
              </div>
            </div>
          )}

          {/* Signature section (print only) */}
          <div className="print-only justify-between mt-12 pt-6 border-t-2 border-gray-300">
            {['Prepared By','Checked By','Approved By'].map(label => (
              <div key={label} className="text-center" style={{ minWidth:160 }}>
                <div className="border-t border-gray-500 pt-1 text-sm text-gray-600">{label}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      <style>{`@keyframes spin { to { transform:rotate(360deg); } }`}</style>
    </div>
  );
};

export default DayBook;